import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import Modal from '../../../Modal';
import styles from './index.module.scss'
import TaskStatusPicker from "../../../Pickers/TaskStatusPicker";
import TimeTrackerPicker from "../../../Pickers/TimeTrackerPicker";
import TaskPriorityPicker from "../../../Pickers/TaskPriorityPicker";
import UsersPicker from "../../../Pickers/UsersPicker";
import DatePicker from "../../../Pickers/TaskEstimatePicker";
import Description from "./components/Description";
import RelatedTask from "./components/RelatedTask";
import relatedStyles from "./components/RelatedTask/index.module.scss";
import Actions from "./components/Actions";
import {Project} from "../../../../types";
import Attachment, { AttachmentList } from "./components/Attachment";
import TimeLogService from "../../../../services/time-log.service";
import TaskService from "../../../../services/task.service";
import DropDown from "../../../DropDown";
import { TASK_TYPES } from './utils/taskTypes';
import { getProjectTaskStatuses } from '../../../../page/Project/utils/taskStatuses';
import { buildTaskUpdatePayload, stripTaskTimeTrackingFields, TASK_SPENT_TIME_FIELDS, updateTaskChangedFields } from '../../../../page/Project/utils/taskPayload';
import { dispatchProjectNotification } from '../../../../utils/projectNotification';
import AttachmentService from '../../../../services/attachment.service';
import { hasPendingInlineImage, hasPendingInlineImages, PendingInlineImage } from '../../../../utils/inlineTaskImages';
import { resizeTextareaToContent } from '../../../../utils/resizeTextareaToContent';
import { persistRelatedTask } from '../../../../utils/persistRelatedTask';
import {
    getDescriptionApiErrorMessage,
    isDescriptionTooLong,
    TASK_DESCRIPTION_LENGTH_ERROR,
    TASK_DESCRIPTION_MAX_LENGTH,
} from './utils/descriptionValidation';
import { useNavigate } from 'react-router';
import { useWorkspace } from '../../../../context/workspace.context';
import { formatTaskTotalSpentTime, getTaskSpentHours, getTaskTotalSpentMinutes } from '../../../../utils/taskSpentTime';
import ConfirmationModal from '../../../ConfirmationModal';
import { getAttachmentUploadErrorMessage, getAttachmentValidationMessage } from '../../../../utils/attachmentValidation';
import ProjectDeleteModal from '../../../ProjectControls/ProjectDeleteModal';
import TaskMoveModal from '../../../ProjectControls/TaskMoveModal';
import ShareIcon from '../../../../assets/icon/ShareIcon';
import DotsVerticalIcon from '../../../../assets/icon/DotsVerticalIcon';
import EditIcon from '../../../../assets/icon/EditIcon';
import DuplicateIcon from '../../../../assets/icon/DuplicateIcon';
import CopyLinkIcon from '../../../../assets/icon/CopyLinkIcon';
import TaskMoveIcon from '../../../../assets/icon/TaskMoveIcon';
import ArchiveIcon from '../../../../assets/icon/ArchiveIcon';
import TrashIcon from '../../../../assets/icon/TrashIcon';
import { copyProjectTaskLink, makeProjectTaskLink } from '../../../../utils/projectLink';
import { moveTaskToProject } from '../../../../page/Project/utils/taskMove';

const getTaskSubmitErrorMessage = (error: any) => {
    const responseData = error?.response?.data ?? error?.data
    const descriptionError = getDescriptionApiErrorMessage(responseData)

    if (descriptionError) {
        return descriptionError
    }

    return getAttachmentUploadErrorMessage(error, undefined, 'Не удалось сохранить задачу. Проверьте данные и попробуйте снова.')
}

interface CreateTaskModalProps {
    task?: any
    isOpen: boolean
    project: Project
    onClose: () => void
    createTask: (task: any) => Promise<any>
    onEditCommitted?: () => void | Promise<void>
    targetCommentId?: string | null
    // When this modal was opened from a parent task's stage, the parent's
    // title is shown as a breadcrumb above the header to return back to it.
    parentTask?: { title?: string } | null
    onReturnToParent?: () => void
    onCloseAll?: () => void
}

const getInitialTaskData = (task?: any) => ({
    title: task?.title ?? '',
    status: null,
    priority: task?.priority || 'medium',
    spendTime: getTaskSpentHours(task),
    timeLogId: task?.timeLogId ?? task?.time_log_id ?? null,
    timeLogDescription: task?.timeLogDescription ?? task?.time_log_description ?? "",
    description: "",
    due_date: null,
    relatedTasks: [null],
    assignee_files: [],
    assignee_ids: [],
    task_type: task?.task_type || 'task',
    ...(task || {})
})

const normalizeDraftDate = (value: any) => {
    if (!value) return null
    if (value instanceof Date) return value.toISOString()
    return String(value)
}

const getCreateTaskDraftSignature = (taskData: any, attachments: any[], description: string) => JSON.stringify({
    title: taskData?.title || '',
    status: taskData?.status ?? null,
    priority: taskData?.priority ?? null,
    spendTime: taskData?.spendTime ?? null,
    description: description || '',
    dueDate: normalizeDraftDate(taskData?.due_date),
    assigneeIds: (taskData?.assignee_ids || []).map(String).sort(),
    taskType: taskData?.task_type || 'task',
    relatedTasks: (taskData?.relatedTasks || []).filter(Boolean).map((relatedTask: any) => ({
        id: relatedTask?.id ?? null,
        title: relatedTask?.title || '',
        status: relatedTask?.status ?? null,
        priority: relatedTask?.priority ?? null,
        spendTime: relatedTask?.spendTime ?? relatedTask?.spend_time ?? null,
        description: relatedTask?.description || '',
        dueDate: normalizeDraftDate(relatedTask?.due_date),
        assigneeIds: (relatedTask?.assignee_ids || []).map(String).sort(),
        taskType: relatedTask?.task_type || 'task',
    })),
    attachments: (attachments || []).map((attachment: any) => ({
        id: attachment?.id ?? null,
        name: attachment?.filename || attachment?.name || '',
        size: attachment?.size ?? null,
        type: attachment?.type || '',
        lastModified: attachment?.lastModified ?? null,
    })),
})

export default function TaskModal({isOpen, onClose, task, project, createTask, onEditCommitted, targetCommentId, parentTask, onReturnToParent, onCloseAll}: CreateTaskModalProps) {
    const navigate = useNavigate()
    const { workspace } = useWorkspace()
    const [taskData, setTaskData] = useState<any>(() => getInitialTaskData(task));
    const [attachments, setAttachments] = useState<any[]>([])
    const taskAttachmentsRef = useRef(attachments)
    taskAttachmentsRef.current = attachments
    const [showTitleError, setShowTitleError] = useState(false)
    const [hasDescriptionError, setHasDescriptionError] = useState(() => isDescriptionTooLong(task?.description))
    const [descriptionServerError, setDescriptionServerError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [taskTypeOpen, setTaskTypeOpen] = useState(false)
    const [openedRelatedTask, setOpenedRelatedTask] = useState<any | null>(null)
    // Parent of a subtask opened directly (board, list, link) rather than from
    // the parent's stages — loaded so the breadcrumb back to it can be shown.
    const [loadedParentTask, setLoadedParentTask] = useState<{ id: number | string, title?: string } | null>(null)
    const parentTaskId = task?.parent_id ?? null
    const hasParentTaskProp = Boolean(parentTask)
    const [activityRefreshVersion, setActivityRefreshVersion] = useState(0)
    const [isDiscardConfirmationOpen, setIsDiscardConfirmationOpen] = useState(false)
    const [isDeleteOpen, setIsDeleteOpen] = useState(false)
    const [isMoveOpen, setIsMoveOpen] = useState(false)
    const isEditMode = !!task?.id
    const hasTitle = Boolean(taskData.title?.trim())
    const totalTimeSpentLabel = formatTaskTotalSpentTime(taskData)
    const titleRef = useRef<HTMLTextAreaElement>(null)
    const modalRootRef = useRef<HTMLDivElement>(null)
    const headerRef = useRef<HTMLDivElement>(null)
    const titleBoxRef = useRef<HTMLDivElement>(null)
    const descriptionDraftRef = useRef(taskData.description || '')
    const taskDataRef = useRef(taskData)
    const backgroundSaveTimerRef = useRef<number | null>(null)
    const backgroundSaveQueueRef = useRef<Promise<void>>(Promise.resolve())
    const backgroundSaveErrorRef = useRef<unknown>(null)
    const closeInProgressRef = useRef(false)
    const modalSessionRef = useRef(0)
    const lastScheduledTaskPayloadRef = useRef('')
    const hasEditChangesRef = useRef(false)
    const pendingInlineImagesRef = useRef<Map<string, File>>(new Map())
    const createTaskDraftBaselineRef = useRef('')
    const pendingCreateCloseActionRef = useRef<(() => void) | null>(null)
    const showTaskError = useCallback((message: string | null) => {
        if (!message) return
        dispatchProjectNotification({ text: message, type: 'error' })
    }, [])
    const refreshParticipantActions = useCallback(() => {
        setActivityRefreshVersion((version) => version + 1)
    }, [])
    const handleInlineImageUpload = useCallback(async (file: File, pendingId: string) => {
        const validationError = getAttachmentValidationMessage(
            [file], taskAttachmentsRef.current.length + pendingInlineImagesRef.current.size,
        )
        if (validationError) throw new Error(validationError)
        pendingInlineImagesRef.current.set(pendingId, file)

        if (!task?.id) {
            return null
        }

        try {
            const attachment = await AttachmentService.upload(task.id, file)
            const attachmentId = attachment?.id

            if (attachmentId === null || attachmentId === undefined) {
                throw new Error('Uploaded inline image has no attachment id')
            }

            return {
                src: attachment.file_url || `/api/attachments/${attachmentId}/file`,
                attachmentId,
                onResolve: () => {
                    setAttachments((current) => current.some((item) => String(item?.id) === String(attachmentId))
                        ? current
                        : [...current, attachment])
                    refreshParticipantActions()
                },
                onDiscard: async () => {
                    await AttachmentService.delete(attachmentId)
                },
            }
        } finally {
            pendingInlineImagesRef.current.delete(pendingId)
        }
    }, [refreshParticipantActions, task?.id])
    const handleInlineImageError = useCallback((error: Error) => {
        showTaskError(error.message || 'Не удалось загрузить изображение')
    }, [showTaskError])
    const enqueueBackgroundSave = useCallback((operation: () => Promise<any>) => {
        const sessionId = modalSessionRef.current
        const queuedSave = backgroundSaveQueueRef.current
            .catch(() => undefined)
            .then(async () => {
                await operation()
                backgroundSaveErrorRef.current = null

                if (sessionId === modalSessionRef.current) {
                    refreshParticipantActions()
                }
            })

        backgroundSaveQueueRef.current = queuedSave.catch((error: any) => {
            if (sessionId !== modalSessionRef.current) return

            console.error('Failed to auto-save task:', error)
            backgroundSaveErrorRef.current = error
            const responseData = error?.response?.data ?? error?.data
            setDescriptionServerError(getDescriptionApiErrorMessage(responseData))
            showTaskError(getTaskSubmitErrorMessage(error))
        })

        return backgroundSaveQueueRef.current
    }, [refreshParticipantActions, showTaskError])
    const persistTaskData = useCallback((nextTaskData: any) => {
        if (
            !isEditMode
            || !task?.id
            || !nextTaskData?.title?.trim()
            || isDescriptionTooLong(nextTaskData?.description)
            || hasPendingInlineImages(nextTaskData?.description)
        ) {
            return Promise.resolve()
        }

        const payload = buildTaskUpdatePayload(nextTaskData, Number(project?.id))
        const payloadSignature = JSON.stringify(payload)

        if (payloadSignature === lastScheduledTaskPayloadRef.current) {
            return Promise.resolve()
        }

        const previousPayload = lastScheduledTaskPayloadRef.current
            ? JSON.parse(lastScheduledTaskPayloadRef.current)
            : {}
        const changedFields = Object.fromEntries(Object.entries(payload).filter(([key, value]) => (
            JSON.stringify(previousPayload[key]) !== JSON.stringify(value)
        )))
        if (!Object.keys(changedFields).length) return Promise.resolve()

        lastScheduledTaskPayloadRef.current = payloadSignature
        return enqueueBackgroundSave(async () => {
            try {
                // PUT expects a complete task. Read its latest values so fields
                // edited by another person are not replaced by this modal's copy.
                await updateTaskChangedFields(task.id, Number(project?.id), changedFields)
                hasEditChangesRef.current = true
            } catch (error) {
                if (lastScheduledTaskPayloadRef.current === payloadSignature) {
                    lastScheduledTaskPayloadRef.current = JSON.stringify(previousPayload)
                }
                throw error
            }
        })
    }, [enqueueBackgroundSave, isEditMode, project?.id, task?.id])
    const scheduleTaskSave = useCallback((nextTaskData: any, delay = 0) => {
        if (!isEditMode) return

        if (backgroundSaveTimerRef.current !== null) {
            window.clearTimeout(backgroundSaveTimerRef.current)
            backgroundSaveTimerRef.current = null
        }

        if (delay <= 0) {
            void persistTaskData(nextTaskData)
            return
        }

        backgroundSaveTimerRef.current = window.setTimeout(() => {
            backgroundSaveTimerRef.current = null
            void persistTaskData(taskDataRef.current)
        }, delay)
    }, [isEditMode, persistTaskData])
    const updateTaskData = useCallback((patch: Record<string, any>, saveDelay = 0, shouldPersist = true) => {
        if (Object.entries(patch).every(([key, value]) => (
            JSON.stringify(taskDataRef.current[key]) === JSON.stringify(value)
        ))) return taskDataRef.current

        const nextTaskData = {
            ...taskDataRef.current,
            ...patch,
        }

        taskDataRef.current = nextTaskData
        setTaskData(nextTaskData)

        if (shouldPersist) {
            scheduleTaskSave(nextTaskData, saveDelay)
        }

        return nextTaskData
    }, [scheduleTaskSave])
    const setDescriptionDraft = useCallback((description: string) => {
        if (descriptionDraftRef.current === description) return
        descriptionDraftRef.current = description
        if (!task?.id) {
            for (const pendingId of pendingInlineImagesRef.current.keys()) {
                if (!hasPendingInlineImage(description, pendingId)) {
                    pendingInlineImagesRef.current.delete(pendingId)
                }
            }
        }
        taskDataRef.current = {
            ...taskDataRef.current,
            description,
        }
        setTaskData((currentTaskData: any) => currentTaskData.description === description
            ? currentTaskData
            : { ...currentTaskData, description })
        setDescriptionServerError((currentError) => currentError ? null : currentError)
        if (!hasPendingInlineImages(description)) {
            scheduleTaskSave(taskDataRef.current, 900)
        }
    }, [scheduleTaskSave, task?.id])
    // Stable per-slot keys so removing a stage from the middle of the list does
    // not make React reuse a sibling's local state for the wrong stage.
    const relatedTaskKeyMap = useRef(new WeakMap<object, string>())
    const relatedTaskKeyCounter = useRef(0)
    const relatedTaskSlotTokensRef = useRef<symbol[]>([])
    const relatedTaskSlotRevisionsRef = useRef(new Map<symbol, number>())
    // Ids of saved stages removed in this session — deleted on the backend when
    // the parent task is saved.
    const deletedRelatedTaskIds = useRef<Array<number | string>>([])

    const getRelatedTaskKey = (relatedTask: any, index: number) => {
        if (relatedTask == null || typeof relatedTask !== 'object') {
            return `related-task-empty-${index}`
        }

        if (relatedTask.id != null) {
            return `related-task-id-${relatedTask.id}`
        }

        let key = relatedTaskKeyMap.current.get(relatedTask)

        if (!key) {
            key = `related-task-${++relatedTaskKeyCounter.current}`
            relatedTaskKeyMap.current.set(relatedTask, key)
        }

        return key
    }

    const resizeTitle = useCallback(() => {
        resizeTextareaToContent(titleRef.current)
    }, [])

    useLayoutEffect(() => {
        resizeTitle()
    }, [taskData.title, isOpen, resizeTitle])

    useEffect(() => {
        const node = titleRef.current
        const container = node?.parentElement
        if (!node || !container) return

        window.addEventListener('resize', resizeTitle)
        if (typeof ResizeObserver === 'undefined') {
            return () => window.removeEventListener('resize', resizeTitle)
        }

        let previousWidth = container.getBoundingClientRect().width
        const observer = new ResizeObserver(([entry]) => {
            const nextWidth = entry.contentRect.width
            if (nextWidth === previousWidth) return
            previousWidth = nextWidth
            resizeTitle()
        })
        observer.observe(container)
        return () => {
            observer.disconnect()
            window.removeEventListener('resize', resizeTitle)
        }
    }, [isOpen, resizeTitle])

    // The description toolbar is sticky: keep it right under whatever is
    // already pinned at the top of the scrolling area — the header while
    // creating, the task title while editing.
    useLayoutEffect(() => {
        const root = modalRootRef.current
        const pinned = isEditMode ? titleBoxRef.current : headerRef.current
        if (!isOpen || !root || !pinned) return

        const update = () => {
            root.style.setProperty('--editor-toolbar-sticky-top', `${pinned.offsetHeight}px`)
        }
        update()

        if (typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver(update)
        observer.observe(pinned)
        return () => observer.disconnect()
    }, [isOpen, isEditMode])

    useEffect(() => {
        setLoadedParentTask(null)
        // Opened from the parent's stages — the parent is already known.
        if (!isOpen || hasParentTaskProp || parentTaskId === null) return

        let isActive = true
        TaskService.get(parentTaskId)
            .then((parent) => {
                if (isActive && parent?.id) setLoadedParentTask({ id: parent.id, title: parent.title })
            })
            .catch(() => {
                // Parent can be deleted or unavailable — just don't show the breadcrumb.
            })

        return () => {
            isActive = false
        }
    }, [isOpen, hasParentTaskProp, parentTaskId])

    useEffect(() => {
        if (!isOpen || !document.fonts) return

        let isActive = true
        document.fonts.ready.then(() => {
            if (isActive) resizeTitle()
        })

        return () => {
            isActive = false
        }
    }, [isOpen, resizeTitle])

    useEffect(() => {
        if (isOpen) {
            modalSessionRef.current += 1

            if (backgroundSaveTimerRef.current !== null) {
                window.clearTimeout(backgroundSaveTimerRef.current)
                backgroundSaveTimerRef.current = null
            }

            const initial = getInitialTaskData(task)
            if (!initial.status) {
                const projectStatuses = getProjectTaskStatuses(project)
                initial.status = projectStatuses[0]?.key || 'backlog'
            }
            taskDataRef.current = initial
            createTaskDraftBaselineRef.current = getCreateTaskDraftSignature(initial, [], initial.description || '')
            lastScheduledTaskPayloadRef.current = task?.id
                ? JSON.stringify(buildTaskUpdatePayload(initial, Number(project?.id)))
                : ''
            hasEditChangesRef.current = false
            backgroundSaveErrorRef.current = null
            closeInProgressRef.current = false
            setTaskData(initial)
            descriptionDraftRef.current = initial.description || ''
            setAttachments([])
            setShowTitleError(false)
            setHasDescriptionError(isDescriptionTooLong(initial.description))
            setDescriptionServerError(null)
            pendingInlineImagesRef.current.clear()
            relatedTaskSlotTokensRef.current = []
            relatedTaskSlotRevisionsRef.current.clear()
            setIsSubmitting(false)
            setTaskTypeOpen(false)
            setOpenedRelatedTask(null)
            setIsDiscardConfirmationOpen(false)
            pendingCreateCloseActionRef.current = null
            deletedRelatedTaskIds.current = []
        }
    }, [isOpen, task?.id, project?.id])

    useEffect(() => () => {
        modalSessionRef.current += 1

        if (backgroundSaveTimerRef.current !== null) {
            window.clearTimeout(backgroundSaveTimerRef.current)
        }
    }, [])

    useEffect(() => {
        if (!isOpen || task?.id) {
            return
        }

        const frameId = window.requestAnimationFrame(() => {
            titleRef.current?.focus()
            titleRef.current?.select()
        })

        return () => window.cancelAnimationFrame(frameId)
    }, [isOpen, task?.id])

    useEffect(() => {
        let isMounted = true

        const loadTimeLog = async () => {
            if (!isOpen || !task?.id) {
                return
            }

            try {
                const timeLog = await TimeLogService.getByTaskId(task.id)

                if (!isMounted) {
                    return
                }

                const minutesSpent = Number(timeLog?.minutes_spent)
                const spendTime = timeLog && !Number.isNaN(minutesSpent)
                    ? minutesSpent / 60
                    : getInitialTaskData(task).spendTime

                setTaskData((prev: any) => {
                    const nextTaskData = {
                        ...prev,
                        spendTime,
                        spend_time: spendTime,
                        timeLogId: timeLog?.id ?? null,
                        timeLogDescription: timeLog?.description ?? '',
                    }
                    taskDataRef.current = nextTaskData
                    return nextTaskData
                })
            } catch (error) {
                console.error('Failed to load task time log:', error)
            }
        }

        void loadTimeLog()

        return () => {
            isMounted = false
        }
    }, [isOpen, task?.id])

    const setStatus = (statusId: string | number) => {
        updateTaskData({ status: statusId })
    }

    const setPriority = (priorityId: string | number) => {
        updateTaskData({ priority: priorityId })
    }

    const setTaskType = (taskType: string) => {
        updateTaskData({ task_type: taskType })
        setTaskTypeOpen(false)
    }

    const setTitle = (title: string) => {
        if (title.trim()) {
            setShowTitleError(false)
        }

        updateTaskData({ title }, 700)
    }


    const setDescription = useCallback((description: string) => {
        descriptionDraftRef.current = description
        updateTaskData({ description }, 300, !hasPendingInlineImages(description))
    }, [updateTaskData])

    const setEstimate = (dueDate: Date) => {
        updateTaskData({ due_date: dueDate })
    }

    const setSpendTime = (spendTime: number) => {
        const previousSpendMinutes = Math.round((getTaskSpentHours(taskDataRef.current) || 0) * 60)
        const nextSpendMinutes = Math.round(spendTime * 60)
        if (previousSpendMinutes === nextSpendMinutes) return
        const nextTotalTimeSpent = Math.max(
            0,
            Math.max(getTaskTotalSpentMinutes(taskDataRef.current), previousSpendMinutes)
                - previousSpendMinutes
                + nextSpendMinutes,
        )
        const nextTaskData = updateTaskData({
            spendTime,
            spend_time: spendTime,
            total_time_spent: nextTotalTimeSpent,
        }, 0, false)

        if (isEditMode && task?.id) {
            void enqueueBackgroundSave(async () => {
                const timeLog = await TimeLogService.syncForTask({
                    taskId: task.id,
                    spendTime,
                    description: nextTaskData?.timeLogDescription || '',
                    timeLogId: nextTaskData?.timeLogId,
                })

                const withTimeLog = {
                    ...taskDataRef.current,
                    timeLogId: timeLog?.id ?? null,
                    time_log_id: timeLog?.id ?? null,
                }
                taskDataRef.current = withTimeLog
                setTaskData(withTimeLog)
                hasEditChangesRef.current = true
            })
        }
    }


    const setUsers = (usersId: number | string) => {
        const currentUsersIds = Array.isArray(taskDataRef.current.assignee_ids) ? taskDataRef.current.assignee_ids : []
        let newUsersId = []

        if (currentUsersIds.some((currentUserId: string | number) => String(currentUserId) === String(usersId))) {
            newUsersId = currentUsersIds.filter((currentUserId: string | number) => String(currentUserId) !== String(usersId))
        } else {
            newUsersId = [...currentUsersIds, usersId]
        }

        updateTaskData({ assignee_ids: newUsersId })
    }


    const addRelatedTask = (newRelatedTask: any, index: number, changedPatch?: Record<string, any>) => {
        const newRelatedTasksList = [...(taskDataRef.current.relatedTasks || [])]
        const previousRelatedTask = newRelatedTasksList[index]
        const wasEmpty = !previousRelatedTask
        const timeWasChanged = changedPatch
            ? TASK_SPENT_TIME_FIELDS.some((field) => Object.prototype.hasOwnProperty.call(changedPatch, field))
            : getTaskSpentHours(newRelatedTask) !== getTaskSpentHours(previousRelatedTask)
        const projectId = Number(newRelatedTask.project_id ?? project.id)
        const previousPayload = previousRelatedTask
            ? buildTaskUpdatePayload(previousRelatedTask, projectId)
            : {}
        const nextPayload = buildTaskUpdatePayload(newRelatedTask, projectId)
        const changedFields = Object.fromEntries(Object.entries(nextPayload).filter(([key, value]) => (
            (!changedPatch || Object.prototype.hasOwnProperty.call(changedPatch, key))
            && JSON.stringify(previousPayload[key]) !== JSON.stringify(value)
        )))
        newRelatedTasksList[index] = newRelatedTask

        const slotToken = relatedTaskSlotTokensRef.current[index] || Symbol(`related-task-${index}`)
        relatedTaskSlotTokensRef.current[index] = slotToken
        const revision = (relatedTaskSlotRevisionsRef.current.get(slotToken) || 0) + 1
        relatedTaskSlotRevisionsRef.current.set(slotToken, revision)

        if (wasEmpty && newRelatedTasksList[newRelatedTasksList.length - 1] !== null) {
            newRelatedTasksList.push(null)
        }

        const nextTaskData = {
            ...taskDataRef.current,
            relatedTasks: newRelatedTasksList
        }
        taskDataRef.current = nextTaskData
        setTaskData(nextTaskData)

        if (isEditMode && task?.id) {
            void enqueueBackgroundSave(async () => {
                const currentIndex = relatedTaskSlotTokensRef.current.indexOf(slotToken)
                const currentRelatedTask = taskDataRef.current.relatedTasks?.[currentIndex]

                if (currentIndex < 0 || !currentRelatedTask) {
                    return
                }

                const persistedTask = await persistRelatedTask({
                    task: previousRelatedTask?.id ? newRelatedTask : currentRelatedTask,
                    parentTaskId: task.id,
                    projectId: currentRelatedTask.project_id ?? project.id,
                    syncTime: timeWasChanged,
                    changes: currentRelatedTask.id ? changedFields : undefined,
                })
                hasEditChangesRef.current = true

                // A later click has already produced a newer optimistic value.
                // Its queued save will apply the final response in order.
                if (relatedTaskSlotRevisionsRef.current.get(slotToken) !== revision) {
                    return
                }

                const latestIndex = relatedTaskSlotTokensRef.current.indexOf(slotToken)
                const latestRelatedTask = taskDataRef.current.relatedTasks?.[latestIndex]

                if (latestIndex < 0 || !latestRelatedTask) {
                    return
                }

                const persistedRelatedTask = {
                    ...latestRelatedTask,
                    ...(persistedTask || {}),
                    ...changedFields,
                    id: persistedTask?.id ?? latestRelatedTask.id,
                }
                const persistedRelatedTasks = [...taskDataRef.current.relatedTasks]
                persistedRelatedTasks[latestIndex] = persistedRelatedTask
                const persistedParentTask = {
                    ...taskDataRef.current,
                    relatedTasks: persistedRelatedTasks,
                }

                taskDataRef.current = persistedParentTask
                setTaskData(persistedParentTask)
            })
        }
    }

    const deleteRelatedTask = (index: number) => {
        const removedRelatedTask = (taskData.relatedTasks || [])[index]
        const [removedToken] = relatedTaskSlotTokensRef.current.splice(index, 1)
        if (removedToken) relatedTaskSlotRevisionsRef.current.delete(removedToken)

        if (removedRelatedTask?.id) {
            deletedRelatedTaskIds.current.push(removedRelatedTask.id)
            hasEditChangesRef.current = true
        }

        const newRelatedTasksList = (taskData.relatedTasks || []).filter((_: any, currentIndex: number) => currentIndex !== index)

        if (newRelatedTasksList.length === 0 || newRelatedTasksList[newRelatedTasksList.length - 1] !== null) {
            newRelatedTasksList.push(null)
        }

        const nextTaskData = {
            ...taskDataRef.current,
            relatedTasks: newRelatedTasksList
        }
        taskDataRef.current = nextTaskData
        setTaskData(nextTaskData)
    }

    const createTaskHandler = async () => {
        if (isSubmitting) {
            return
        }

        if (!hasTitle) {
            setShowTitleError(true)
            return
        }

        const currentDescriptionError = isDescriptionTooLong(descriptionDraftRef.current)
            ? TASK_DESCRIPTION_LENGTH_ERROR
            : descriptionServerError

        if (currentDescriptionError) {
            showTaskError(currentDescriptionError)
            return
        }

        if (isEditMode && hasPendingInlineImages(descriptionDraftRef.current)) {
            showTaskError('Дождитесь завершения загрузки изображения')
            return
        }

        const pendingImages = Array.from(pendingInlineImagesRef.current, ([pendingId, file]): PendingInlineImage => ({ pendingId, file }))
            .filter(({ pendingId }) => hasPendingInlineImage(descriptionDraftRef.current, pendingId))
        const attachmentError = getAttachmentValidationMessage(
            [...attachments.filter((file) => file instanceof File), ...pendingImages.map(({ file }) => file)],
            attachments.filter((file) => !(file instanceof File)).length,
        )
        if (attachmentError) {
            showTaskError(attachmentError)
            return
        }

        setIsSubmitting(true)

        try {
            await createTask({
                ...taskData,
                description: descriptionDraftRef.current,
                assignee_files: attachments,
                inline_images: pendingImages,
                deletedRelatedTaskIds: [...deletedRelatedTaskIds.current],
            })
        } catch (error: any) {
            const errorMessage = getTaskSubmitErrorMessage(error)
            const responseData = error?.response?.data ?? error?.data

            setDescriptionServerError(getDescriptionApiErrorMessage(responseData))
            showTaskError(errorMessage)
        } finally {
            setIsSubmitting(false)
        }
    }

    const updateRelatedTask = async (relatedTaskData: any) => {
        if (!relatedTaskData?.id) {
            return relatedTaskData
        }

        const updatedTask = await TaskService.update(relatedTaskData.id, stripTaskTimeTrackingFields(relatedTaskData))

        const nextRelatedTask = {
            ...relatedTaskData,
            ...(updatedTask || {}),
        }

        setTaskData((prev: any) => {
            const nextTaskData = {
                ...prev,
                relatedTasks: (prev.relatedTasks || []).map((currentTask: any) => (
                currentTask?.id === relatedTaskData.id
                    ? nextRelatedTask
                    : currentTask
                )),
            }
            taskDataRef.current = nextTaskData
            return nextTaskData
        })
        setOpenedRelatedTask(null)

        return nextRelatedTask
    }

    const hasUnsavedCreateChanges = () => (
        !isEditMode
        && createTaskDraftBaselineRef.current !== getCreateTaskDraftSignature(
            taskDataRef.current,
            attachments,
            descriptionDraftRef.current,
        )
    )

    const finishCreateTaskClose = () => {
        const pendingAction = pendingCreateCloseActionRef.current
        pendingCreateCloseActionRef.current = null
        setIsDiscardConfirmationOpen(false)
        onClose()
        pendingAction?.()
    }

    const cancelCreateTaskClose = () => {
        pendingCreateCloseActionRef.current = null
        setIsDiscardConfirmationOpen(false)
    }

    const handleClose = async (): Promise<boolean> => {
        if (closeInProgressRef.current) return false
        if (!isEditMode) {
            if (hasUnsavedCreateChanges()) {
                setIsDiscardConfirmationOpen(true)
                return false
            }

            finishCreateTaskClose()
            return true
        }

        if (isEditMode && hasTitle) {
            if (hasPendingInlineImages(descriptionDraftRef.current)) {
                showTaskError('Дождитесь завершения загрузки изображения')
                return false
            }

            const currentDescriptionError = isDescriptionTooLong(descriptionDraftRef.current)
                ? TASK_DESCRIPTION_LENGTH_ERROR
                : descriptionServerError

            if (currentDescriptionError) {
                showTaskError(currentDescriptionError)
                return false
            }

            closeInProgressRef.current = true
            try {
                if (backgroundSaveTimerRef.current !== null) {
                    window.clearTimeout(backgroundSaveTimerRef.current)
                    backgroundSaveTimerRef.current = null
                }

                await persistTaskData({
                    ...taskDataRef.current,
                    description: descriptionDraftRef.current,
                })
                await backgroundSaveQueueRef.current
                if (backgroundSaveErrorRef.current) throw backgroundSaveErrorRef.current
                if (deletedRelatedTaskIds.current.length) {
                    await Promise.all(deletedRelatedTaskIds.current.map((id) => TaskService.delete(Number(id))))
                    deletedRelatedTaskIds.current = []
                }
                if (hasEditChangesRef.current) {
                    await onEditCommitted?.()
                }
                onClose()
                return true
            } catch (error) {
                console.error('Failed to auto-update task:', error)
                const errorMessage = getTaskSubmitErrorMessage(error)
                const responseData = (error as any)?.response?.data ?? (error as any)?.data

                setDescriptionServerError(getDescriptionApiErrorMessage(responseData))
                showTaskError(errorMessage)
                return false
            } finally {
                closeInProgressRef.current = false
            }
        }
        onClose()
        return true
    }

    const copyTaskLink = async () => {
        if (!task?.id) return
        try {
            await copyProjectTaskLink(project.id, task.id, 'board')
            dispatchProjectNotification({ text: 'Ссылка скопирована' })
        } catch (error) {
            console.error('Failed to copy task link:', error)
        }
    }

    const shareTask = async () => {
        if (!task?.id) return
        const url = makeProjectTaskLink(project.id, task.id, 'board')
        if (navigator.share) {
            try {
                await navigator.share({ title: taskDataRef.current.title || task.title, url })
                return
            } catch (error) {
                if ((error as DOMException)?.name === 'AbortError') return
            }
        }
        await copyTaskLink()
    }

    const duplicateTask = async () => {
        const sourceTask = taskDataRef.current
        if (!await handleClose()) return
        try {
            const duplicatedTask = await TaskService.create(buildTaskUpdatePayload(sourceTask, project.id, {
                title: `${sourceTask.title || 'Задача'} (копия)`,
            }))
            const relatedTasks = Array.isArray(sourceTask.relatedTasks)
                ? sourceTask.relatedTasks.filter(Boolean)
                : []
            await Promise.all(relatedTasks.map((relatedTask: any) => TaskService.create(buildTaskUpdatePayload(relatedTask, project.id, {
                parent_id: duplicatedTask.id,
            }))))
            await onEditCommitted?.()
            dispatchProjectNotification({
                text: 'Задача успешно продублирована',
                action: {
                    label: 'Отменить',
                    onClick: () => {
                        void (async () => {
                            try {
                                await TaskService.delete(duplicatedTask.id)
                                await onEditCommitted?.()
                            } catch (error) {
                                console.error('Failed to undo task duplication:', error)
                            }
                        })()
                    },
                },
            })
        } catch (error) {
            console.error('Failed to duplicate task:', error)
        }
    }

    const moveTask = async (targetProject: Project) => {
        const sourceTask = taskDataRef.current
        if (!await handleClose()) return
        try {
            await moveTaskToProject(sourceTask, Number(project.id), targetProject)
            await onEditCommitted?.()
            dispatchProjectNotification({ text: 'Задача перемещена в другой проект' })
        } catch (error) {
            console.error('Failed to move task:', error)
        }
    }

    const archiveTask = async () => {
        const archived = Boolean(taskDataRef.current.is_archived || taskDataRef.current.archived_at)
        if (!await handleClose()) return
        try {
            if (archived) {
                await TaskService.unarchive(task.id)
            } else {
                await TaskService.archive(task.id)
            }
            await onEditCommitted?.()
            if (!archived) {
                dispatchProjectNotification({
                    text: 'Задача перенесена в архив',
                    action: {
                        label: 'Отменить',
                        onClick: () => {
                            void (async () => {
                                try {
                                    await TaskService.unarchive(task.id)
                                    await onEditCommitted?.()
                                } catch (error) {
                                    console.error('Failed to undo task archive:', error)
                                }
                            })()
                        },
                    },
                })
            }
        } catch (error) {
            console.error('Failed to change task archive state:', error)
        }
    }

    const deleteTask = async () => {
        if (!await handleClose()) return
        try {
            await TaskService.delete(task.id)
            await onEditCommitted?.()
        } catch (error) {
            console.error('Failed to delete task:', error)
        }
    }

    const handleBreadcrumbNavigate = async (path: string) => {
        if (!isEditMode) {
            pendingCreateCloseActionRef.current = () => {
                onCloseAll?.()
                navigate(path)
            }
            await handleClose()
            return
        }

        const canNavigate = await handleClose()

        if (!canNavigate) {
            return
        }

        onCloseAll?.()
        navigate(path)
    }

    const breadcrumbParent = parentTask || loadedParentTask
    const isSubtask = Boolean(parentTask || parentTaskId)

    const selectedTaskType = TASK_TYPES.find((type) => type.id === taskData.task_type) || TASK_TYPES[0]
    const projectStatuses = useMemo(() => getProjectTaskStatuses(project), [project])

    return (
        <>
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            shouldCloseOnEsc={!isDiscardConfirmationOpen && !isDeleteOpen && !isMoveOpen}
            shouldCloseOnOverlayClick={!isDiscardConfirmationOpen && !isDeleteOpen && !isMoveOpen}
            contentLabel={isEditMode ? 'Редактирование задачи' : 'Создание задачи'}
            width={task?.id ? 1180 : 690}
            // Edit modal keeps a fixed proportion of the viewport height
            // (1280/1920 ≈ 66.67vh) so it scales with the browser window.
            height={task?.id ? '66.67vh' : undefined}
        >
            <div
                ref={modalRootRef}
                className={`${styles.createTaskModal} ${task?.id ? styles.createTaskModalEdit : ''}`}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
            >
                <div className={styles.createTaskModal__header} ref={headerRef}>
                    <nav className={styles.createTaskModal__path} aria-label="Хлебные крошки">
                        <button
                            type="button"
                            className={styles.createTaskModal__pathLink}
                            onClick={() => void handleBreadcrumbNavigate('/workspaces')}
                        >
                            {workspace?.name || 'Рабочее пространство'}
                        </button>
                        <span className={styles.createTaskModal__pathSeparator}>/</span>
                        <button
                            type="button"
                            className={styles.createTaskModal__pathLink}
                            onClick={() => void handleBreadcrumbNavigate('/projects')}
                        >
                            Проекты
                        </button>
                        <span className={styles.createTaskModal__pathSeparator}>/</span>
                        <button
                            type="button"
                            className={`${styles.createTaskModal__pathLink} ${styles.createTaskModal__pathCurrent}`}
                            onClick={() => void handleBreadcrumbNavigate(`/projects/${project.id}`)}
                        >
                            {project?.name || 'Проект'}
                        </button>
                    </nav>
                    <div className={styles.createTaskModal__headerActions}>
                        {isEditMode && (
                            <>
                                <DropDown
                                    closeDropDownByPick
                                    position="right"
                                    btn={<button type="button" className={styles.createTaskModal__headerIcon} aria-label="Действия с задачей"><DotsVerticalIcon /></button>}
                                >
                                    <div className={styles.createTaskModal__taskMenu}>
                                        <button type="button" className={styles.createTaskModal__taskMenuItem} onClick={() => { titleRef.current?.focus(); titleRef.current?.select() }}><EditIcon /><span>Переименовать</span></button>
                                        <button type="button" className={styles.createTaskModal__taskMenuItem} onClick={() => void duplicateTask()}><DuplicateIcon /><span>Дублировать</span></button>
                                        <button type="button" className={styles.createTaskModal__taskMenuItem} onClick={() => void copyTaskLink()}><CopyLinkIcon /><span>Копировать ссылку</span></button>
                                        <button type="button" className={styles.createTaskModal__taskMenuItem} onClick={() => setIsMoveOpen(true)}><TaskMoveIcon /><span>Переместить</span></button>
                                        <button type="button" className={styles.createTaskModal__taskMenuItem} onClick={() => void archiveTask()}><ArchiveIcon /><span>{taskData.is_archived || taskData.archived_at ? 'Вернуть с архива' : 'Архивировать'}</span></button>
                                        <button type="button" className={`${styles.createTaskModal__taskMenuItem} ${styles.createTaskModal__taskMenuItemDanger}`} onClick={() => setIsDeleteOpen(true)}><TrashIcon /><span>Удалить</span></button>
                                    </div>
                                </DropDown>
                                <button type="button" className={styles.createTaskModal__headerIcon} aria-label="Поделиться задачей" onClick={() => void shareTask()}><ShareIcon /></button>
                            </>
                        )}
                        <button
                            type="button"
                            className={styles.createTaskModal__close}
                            aria-label="Закрыть форму"
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                event.stopPropagation()
                                handleClose()
                            }}
                        >
                            <img src="/icon/close.svg" alt="close"/>
                        </button>
                    </div>
                </div>
                <div className={`${styles.createTaskModal__body} ${task?.id ? styles.createTaskModal__bodyWithSidebar : ''}`}>
                    <div className={`${styles.createTaskModal__main} ${task?.id ? styles.createTaskModal__mainWithSidebar : ''}`}>
                        <div className={styles.createTaskModal__typeRow}>
                            <span className={styles.createTaskModal__typeLabel}>Тип задачи</span>
                            <DropDown
                                isOpen={taskTypeOpen}
                                setIsOpen={setTaskTypeOpen}
                                position="left"
                                dropdownClassName={styles.createTaskModal__typeDropdown}
                                contentClassName={styles.createTaskModal__typeDropdownContent}
                                btn={(
                                    <button type="button" className={styles.createTaskModal__typePill}>
                                        <span className={styles.createTaskModal__typeIcon}>{selectedTaskType.icon}</span>
                                        <span>{selectedTaskType.label}</span>
                                    </button>
                                )}
                            >
                                <div className={styles.createTaskModal__typeMenu}>
                                    {TASK_TYPES.map((type) => (
                                        <button
                                            key={type.id}
                                            type="button"
                                            className={`${styles.createTaskModal__typeOption} ${taskData.task_type === type.id ? styles.createTaskModal__typeOptionActive : ''}`}
                                            onClick={() => setTaskType(type.id)}
                                        >
                                            <span className={styles.createTaskModal__typeIcon}>{type.icon}</span>
                                            <span>{type.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </DropDown>
                            {isSubtask && (
                                <span className={styles.createTaskModal__subtaskBadge}>Подзадача</span>
                            )}
                            {isEditMode && (
                                <div
                                    className={styles.createTaskModal__totalTime}
                                    aria-label={`Общее время: ${totalTimeSpentLabel}`}
                                    aria-disabled="true"
                                >
                                    <span>Общее время:</span>
                                    <span className={styles.createTaskModal__totalTimeValue}>{totalTimeSpentLabel}</span>
                                </div>
                            )}
                        </div>
                        {breadcrumbParent && (
                            <button
                                type="button"
                                className={styles.createTaskModal__breadcrumb}
                                title={breadcrumbParent.title ? `Родительская задача: ${breadcrumbParent.title}` : undefined}
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={(event) => {
                                    event.stopPropagation()
                                    if (parentTask) {
                                        void handleClose()
                                        return
                                    }
                                    if (loadedParentTask) void handleBreadcrumbNavigate(`/projects/${project.id}?task=${loadedParentTask.id}`)
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M3.76129 5.84C3.61912 5.97248 3.43107 6.0446 3.23677 6.04117C3.04247 6.03775 2.85708 5.95903 2.71967 5.82162C2.58226 5.68421 2.50355 5.49882 2.50012 5.30452C2.49669 5.11022 2.56881 4.92218 2.70129 4.78L5.95129 1.53C6.244 1.23729 6.71858 1.23729 7.01129 1.53L10.2613 4.78C10.3938 4.92218 10.4659 5.11022 10.4625 5.30452C10.459 5.49882 10.3803 5.68421 10.2429 5.82162C10.1055 5.95903 9.92011 6.03775 9.72581 6.04117C9.53151 6.0446 9.34347 5.97248 9.20129 5.84L7.23129 3.87V12C7.23129 12.3978 7.38933 12.7794 7.67063 13.0607C7.95194 13.342 8.33347 13.5 8.73129 13.5H12.7313C12.9302 13.5 13.121 13.579 13.2616 13.7197C13.4023 13.8603 13.4813 14.0511 13.4813 14.25C13.4813 14.4489 13.4023 14.6397 13.2616 14.7803C13.121 14.921 12.9302 15 12.7313 15H8.73129C7.93564 15 7.17258 14.6839 6.60997 14.1213C6.04736 13.5587 5.73129 12.7956 5.73129 12V3.87L3.76129 5.84Z" fill="currentColor"/>
                                </svg>
                                <span className={styles.createTaskModal__breadcrumbTitle}>
                                    {breadcrumbParent.title || 'Родительская задача'}
                                </span>
                            </button>
                        )}
                        <div ref={titleBoxRef} className={`${styles.createTaskModal__input} ${showTitleError ? styles.createTaskModal__inputError : ''}`}>
                            <textarea
                                ref={titleRef}
                                className={styles.createTaskModal__inputField}
                                placeholder={" "}
                                rows={1}
                                wrap="soft"
                                value={taskData.title || ''}
                                onChange={e => setTitle(e.target.value)}
                            />
                            <span className={`${styles.createTaskModal__inputLabel} ${hasTitle ? styles.createTaskModal__inputLabelHidden : ''}`}>
                                Название задачи
                                <span className={styles.createTaskModal__inputAsterisk}>*</span>
                            </span>
                            <div
                                className={styles.createTaskModal__inputIcon}
                                onClick={() => {
                                    const node = titleRef.current
                                    if (!node) return
                                    const end = node.value.length
                                    node.focus()
                                    node.setSelectionRange(end, end)
                                }}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M20.4695 19.9991H9.98339C9.53815 19.9991 9.17639 19.6373 9.17639 19.1921C9.17639 18.7468 9.53815 18.3851 9.98339 18.3851H20.4695C20.9147 18.3851 21.2765 18.7468 21.2765 19.1921C21.2765 19.6385 20.9147 19.9991 20.4695 19.9991Z"
                                        fill="#64617E"/>
                                    <path
                                        d="M18.2643 5.59826L17.1233 4.45734C16.5136 3.84755 15.4513 3.84755 14.8427 4.45734L13.1743 6.12577L16.5958 9.54733L18.2643 7.8789C18.5691 7.57401 18.7373 7.16991 18.7373 6.73798C18.7373 6.30847 18.5691 5.90315 18.2643 5.59826Z"
                                        fill="#64617E"/>
                                    <path
                                        d="M12.0346 7.26714L3.72143 15.5803C3.6682 15.6335 3.63069 15.7001 3.61375 15.7727L2.73416 19.5039C2.70271 19.6395 2.74263 19.7822 2.84184 19.8814C2.91807 19.9577 3.02091 20 3.12738 20C3.15762 20 3.18908 19.9964 3.21933 19.9891L6.95062 19.1095C7.02321 19.0926 7.09097 19.0551 7.14299 19.0018L15.4561 10.6887L12.0346 7.26714Z"
                                        fill="#64617E"/>
                                </svg>

                            </div>
                        </div>
                        <div className={styles.createTaskModal__plate}>
                            <div className={styles.createTaskModal__plate_item}>
                                <TaskStatusPicker getStatus={setStatus} pickedId={taskData.status} taskData={taskData} statuses={projectStatuses}/>
                            </div>
                            <div className={styles.createTaskModal__plate_item}>
                                <DatePicker setEstimate={setEstimate} estimate={taskData.due_date}/>
                            </div>
                            <div className={styles.createTaskModal__plate_item}>
                                <UsersPicker
                                    getUsers={setUsers}
                                    usersId={taskData.assignee_ids}
                                    selectedUsers={taskData.users || taskData.assignees || []}
                                    projectId={project?.id}
                                    prioritizeSelected={isEditMode}
                                    openDownward
                                    fixedDropdownHeight
                                />
                            </div>
                            <div className={styles.createTaskModal__plate_item}>
                                <TaskPriorityPicker getPriority={setPriority} priorityId={taskData.priority}/>
                            </div>
                            <div className={styles.createTaskModal__plate_item}>
                                <TimeTrackerPicker getSpendTime={setSpendTime} spendTime={taskData.spendTime}/>
                            </div>
                        </div>
                        <div className={styles.createTaskModal__group}>
                            <Description
                                key={task?.id ?? 'new'}
                                isActive={isOpen}
                                saveDescription={setDescription}
                                onDraftChange={setDescriptionDraft}
                                onValidationChange={setHasDescriptionError}
                                description={taskData.description}
                                maxLength={TASK_DESCRIPTION_MAX_LENGTH}
                                errorMessage={TASK_DESCRIPTION_LENGTH_ERROR}
                                serverError={descriptionServerError}
                                onInlineImageUpload={handleInlineImageUpload}
                                onInlineImageError={handleInlineImageError}
                            />
                        </div>
                        <div className={styles.createTaskModal__group}>
                            <div className={styles.createTaskModal__group_title}>
                                Этапы
                            </div>
                            {taskData.relatedTasks.some((t: any) => !!t) && (
                                <div className={relatedStyles.savedHeader}>
                                    <span className={relatedStyles.savedHeader__name}>Название этапа</span>
                                    <span className={relatedStyles.savedHeader__col}>Участники</span>
                                    <span className={relatedStyles.savedHeader__col}>Срок</span>
                                    <span className={relatedStyles.savedHeader__col}>Приоритет</span>
                                    <span className={relatedStyles.savedHeader__col}>Учет време...</span>
                                </div>
                            )}
                            {taskData.relatedTasks.map((currentRelatedTask: any, index: number) => (
                                <div className={styles.createTaskModal__group_item} key={getRelatedTaskKey(currentRelatedTask, index)}>
                                    <RelatedTask
                                        project={project}
                                        onOpenTask={setOpenedRelatedTask}
                                        onDelete={() => deleteRelatedTask(index)}
                                        addRelatedTask={(newRelatedTask: any, changes?: Record<string, any>) => addRelatedTask(newRelatedTask, index, changes)}
                                        currentRelatedTask={currentRelatedTask}/>
                                </div>
                            ))}
                        </div>
                        <AttachmentList
                            attachments={attachments}
                            setAttachments={setAttachments}
                            taskId={taskData?.id}
                            onActivityChange={refreshParticipantActions}
                            headerAction={isEditMode ? (
                                <Attachment
                                    taskId={taskData?.id}
                                    attachments={attachments}
                                    setAttachments={setAttachments}
                                    pendingAttachmentsCount={pendingInlineImagesRef.current.size}
                                    onError={showTaskError}
                                    onActivityChange={refreshParticipantActions}
                                />
                            ) : undefined}
                        />
                    </div>
                    {task?.id && (
                        <div className={styles.createTaskModal__sidebar}>
                            <Actions
                                key={task.id}
                                taskId={task?.id}
                                projectId={project?.id}
                                refreshVersion={activityRefreshVersion}
                                targetCommentId={targetCommentId}
                            />
                        </div>
                    )}
                </div>

                {!isEditMode && (
                    <div className={styles.createTaskModal__actions}>
                        <div className={styles.createTaskModal__actions_attach}>
                            <Attachment
                                taskId={taskData?.id}
                                attachments={attachments}
                                setAttachments={setAttachments}
                                pendingAttachmentsCount={pendingInlineImagesRef.current.size}
                                onError={showTaskError}
                                onActivityChange={refreshParticipantActions}
                            />
                        </div>
                        <div className={styles.createTaskModal__actions_cancel} onClick={() => void handleClose()}>
                            Отменить
                        </div>
                        <div
                            className={`${styles.createTaskModal__actions_ok} ${!hasTitle || hasDescriptionError || descriptionServerError || isSubmitting ? styles.createTaskModal__actions_okDisabled : ''}`}
                            onClick={createTaskHandler}
                            aria-disabled={!hasTitle || hasDescriptionError || Boolean(descriptionServerError) || isSubmitting}
                        >
                            {isSubmitting ? "Сохранение..." : task?.id ? "Обновить" : "Создать"}
                        </div>
                    </div>
                )}
                {openedRelatedTask && (
                    <TaskModal
                        isOpen={!!openedRelatedTask}
                        onClose={() => setOpenedRelatedTask(null)}
                        task={openedRelatedTask}
                        project={project}
                        createTask={updateRelatedTask}
                        onEditCommitted={async () => {
                            const latest = await TaskService.get(openedRelatedTask.id)
                            hasEditChangesRef.current = true
                            setTaskData((previous: any) => {
                                const next = {
                                    ...previous,
                                    relatedTasks: (previous.relatedTasks || []).map((related: any) => (
                                        related?.id === latest.id ? { ...related, ...latest } : related
                                    )),
                                }
                                taskDataRef.current = next
                                return next
                            })
                        }}
                        parentTask={{ title: taskData.title }}
                        onReturnToParent={() => setOpenedRelatedTask(null)}
                        onCloseAll={onCloseAll || onClose}
                    />
                )}
            </div>
        </Modal>
        {isEditMode && (
            <>
                <TaskMoveModal
                    currentProjectId={project.id}
                    isOpen={isMoveOpen}
                    onClose={() => setIsMoveOpen(false)}
                    onConfirm={moveTask}
                />
                <ProjectDeleteModal
                    isOpen={isDeleteOpen}
                    title="Удалить задачу?"
                    text={`Вы уверены, что хотите удалить задачу «${taskData.title || 'Без названия'}»? Это действие нельзя отменить.`}
                    onClose={() => setIsDeleteOpen(false)}
                    onConfirm={deleteTask}
                />
            </>
        )}
        <ConfirmationModal
            isOpen={isOpen && isDiscardConfirmationOpen}
            title="Закрыть форму?"
            text="Вы собираетесь закрыть форму. Все внесенные данные не сохранятся."
            cancelLabel="Вернуться к форме"
            confirmLabel="Закрыть без сохранения"
            confirmTone="danger"
            onCancel={cancelCreateTaskClose}
            onConfirm={finishCreateTaskClose}
        />
        </>
    )
}
